/**
 * Utilidades y funciones auxiliares para la aplicación
 */

/**
 * Extrae el ID interno real del capítulo
 * Ejemplo: de "capitulo-capitulo-1-opciones" se obtiene "chapter-1"
 * 
 * @param {string} section - Sección o ruta actual
 * @returns {string} - ID interno del capítulo
 */
export const extractChapterId = (section) => {
  let s = section;
  if (s.startsWith("capitulo-")) {
    s = s.replace("capitulo-", "");
  }
  if (s.startsWith("capitulo-")) {
    s = s.replace("capitulo-", "chapter-");
  }
  const suffixes = ["opciones", "comic", "articulos", "extras"];
  for (const suf of suffixes) {
    const pattern = new RegExp(`-${suf}$`);
    if (pattern.test(s)) {
      return s.replace(pattern, "");
    }
  }
  return s;
};

/**
 * Ajusta el viewport para dispositivos móviles
 * Soluciona problemas con barras de navegación móviles que cambian el tamaño del viewport
 * 
 * @returns {Function} - Función para limpiar los event listeners
 */
export const setupMobileViewport = () => {
  const setVh = () => {
    const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    document.documentElement.style.setProperty("--vh", `${vh * 0.01}px`);
  };

  setVh();

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", setVh);
    window.visualViewport.addEventListener("scroll", setVh);
  } else {
    window.addEventListener("resize", setVh);
  }
  
  return () => {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", setVh);
      window.visualViewport.removeEventListener("scroll", setVh);
    } else {
      window.removeEventListener("resize", setVh);
    }
  };
};

/**
 * Verifica si la pantalla es pequeña (móvil)
 * 
 * @param {number} breakpoint - Punto de quiebre en píxeles (por defecto 768px)
 * @returns {boolean} - True si la pantalla es pequeña
 */
export const isSmallScreen = (breakpoint = 768) => {
  return window.innerWidth < breakpoint;
};

/**
 * Detecta el tipo de dispositivo
 * 
 * @returns {Object} - Objeto con propiedades que indican el tipo de dispositivo
 */
export const detectDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  return {
    isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent),
    isIOS: /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream,
    isAndroid: /Android/i.test(userAgent),
    isTablet: /iPad/i.test(userAgent) || (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent))
  };
};

/**
 * Formatea una fecha en formato legible
 * 
 * @param {string|Date} date - Fecha a formatear
 * @param {string} locale - Locale para el formato (por defecto 'es-ES')
 * @returns {string} - Fecha formateada
 */
export const formatDate = (date, locale = 'es-ES') => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  return dateObj.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Crear un objeto con todas las utilidades
const helpers = {
  extractChapterId,
  setupMobileViewport,
  isSmallScreen,
  detectDevice,
  formatDate
};

export default helpers; 